import { isFlightSavedItemData, type TransportOption } from 'tripPlanner/api/types'

const AIRLINE_CODE_PATTERN = /^[A-Z0-9]{2}$/
const FULL_FLIGHT_CODE_PATTERN = /^([A-Z]{2,3}|[A-Z][0-9])\s*-?\s*(\d+[A-Z]?)$/i
const NUMERIC_FLIGHT_NUMBER_PATTERN = /^\d+[A-Z]?$/i

function getStringProperty(value: object | undefined, property: string): string | undefined {
  if (!value || !(property in value)) return undefined
  const propertyValue = (value as Record<string, unknown>)[property]
  return typeof propertyValue === 'string' ? propertyValue : undefined
}

function getPrimaryFlightNumber(flightNumber: string | undefined): string | undefined {
  return flightNumber?.split('/').map(part => part.trim()).find(Boolean)
}

function formatFlightCode(savedItemData: object | undefined): string | undefined {
  const flightNumber = getPrimaryFlightNumber(getStringProperty(savedItemData, 'flightNumber'))
  if (!flightNumber) return undefined

  const fullCodeMatch = flightNumber.match(FULL_FLIGHT_CODE_PATTERN)
  if (fullCodeMatch) {
    return `${fullCodeMatch[1].toUpperCase()}${fullCodeMatch[2].toUpperCase()}`
  }

  const airlineCode = getStringProperty(savedItemData, 'airlineCode')?.trim().toUpperCase()
  if (airlineCode && AIRLINE_CODE_PATTERN.test(airlineCode) && NUMERIC_FLIGHT_NUMBER_PATTERN.test(flightNumber)) {
    return `${airlineCode}${flightNumber.toUpperCase()}`
  }

  return undefined
}

function getFlightCodes(option: TransportOption): string | undefined {
  const savedItemData = isFlightSavedItemData(option.savedItemData) ? option.savedItemData : undefined
  const returnSavedItemData = isFlightSavedItemData(option.returnLeg?.savedItemData) ? option.returnLeg.savedItemData : undefined
  return [
    formatFlightCode(savedItemData),
    formatFlightCode(returnSavedItemData),
  ].filter(Boolean).join(' / ') || undefined
}

function getFlightRoute(option: TransportOption): string | undefined {
  const savedItemData = isFlightSavedItemData(option.savedItemData) ? option.savedItemData : undefined
  if (!savedItemData?.departureAirport || !savedItemData.arrivalAirport) return undefined

  const routeSeparator = option.returnLeg ? '⇄' : '→'
  return `${savedItemData.departureAirport} ${routeSeparator} ${savedItemData.arrivalAirport}`
}

export function buildFlightName(option: TransportOption, fallback: string): string {
  const saved = isFlightSavedItemData(option.savedItemData) ? option.savedItemData : undefined
  if (!saved) return option.label || fallback

  const title = [getFlightRoute(option), getFlightCodes(option)].filter(Boolean).join(' · ')
  if (title) return title

  return getPrimaryFlightNumber(getStringProperty(saved, 'flightNumber')) ?? (option.label || fallback)
}
